import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { BarChart3, Users, Shield } from 'lucide-react';
import { rosterAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PlayerStatsTab = ({ videos }) => {
    const { user } = useAuth();
    const [players, setPlayers] = React.useState([]);

    React.useEffect(() => {
        if (user?.team_id) {
            rosterAPI.getRoster(user.team_id).then(res => {
                setPlayers(res.data.filter(m => m.role === 'player'));
            });
        }
    }, [user?.team_id]);

    const { offensiveGroups, defensiveGroups } = useMemo(() => {
        const offenseMap = {};
        const defenseMap = {};

        videos.forEach(video => {
            const playerStats = video.play_data?.player_stats || [];
            playerStats.forEach(stat => {
                const isOffensive = ['passing', 'rushing', 'receiving', 'touchdowns'].includes(stat.stat_type);
                const isDefensive = ['tackles', 'sacks', 'interceptions', 'pass_deflections', 'forced_fumbles'].includes(stat.stat_type);

                if (isOffensive) {
                    if (!offenseMap[stat.player_id]) {
                        const player = players.find(p => p.user_id === stat.player_id);
                        offenseMap[stat.player_id] = {
                            name: stat.player_name,
                            passing: 0, rushing: 0, receiving: 0, touchdowns: 0,
                            positions: player?.positions || []
                        };
                    }
                    offenseMap[stat.player_id][stat.stat_type] += stat.value;
                }

                if (isDefensive) {
                    if (!defenseMap[stat.player_id]) {
                        const player = players.find(p => p.user_id === stat.player_id);
                        defenseMap[stat.player_id] = {
                            name: stat.player_name,
                            tackles: 0, sacks: 0, interceptions: 0, pass_deflections: 0, forced_fumbles: 0,
                            positions: player?.positions || []
                        };
                    }
                    defenseMap[stat.player_id][stat.stat_type] += stat.value;
                }
            });
        });

        const sortStats = (stats, isDefense = false) => {
            return Object.values(stats).sort((a, b) => {
                if (isDefense) {
                    return (b.tackles + b.sacks + b.interceptions + b.pass_deflections + b.forced_fumbles) -
                        (a.tackles + a.sacks + a.interceptions + a.pass_deflections + a.forced_fumbles);
                }
                return (b.passing + b.rushing + b.receiving) - (a.passing + a.rushing + a.receiving);
            });
        };

        const groupByPosition = (stats, categories) => {
            const groups = {};
            categories.forEach(cat => groups[cat.label] = []);

            stats.forEach(player => {
                const category = categories.find(cat =>
                    cat.positions.some(pos => player.positions.includes(pos))
                ) || categories[categories.length - 1]; // Default to last category (Other/Athlete)
                groups[category.label].push(player);
            });

            return Object.entries(groups).filter(([_, players]) => players.length > 0);
        };

        const OFFENSE_CATS = [
            { label: 'Quarterbacks', positions: ['QB'] },
            { label: 'WR / TE', positions: ['WR', 'TE'] },
            { label: 'Running Backs', positions: ['RB', 'FB', 'HB'] },
            { label: 'Offensive Line', positions: ['OL', 'C', 'G', 'T', 'OG', 'OT'] },
            { label: 'Other', positions: [] }
        ];

        const DEFENSE_CATS = [
            { label: 'Defensive Line', positions: ['DL', 'DE', 'DT', 'NT'] },
            { label: 'Linebackers', positions: ['LB', 'MLB', 'OLB'] },
            { label: 'Defensive Backs', positions: ['DB', 'CB', 'S', 'FS', 'SS'] },
            { label: 'Other', positions: [] }
        ];

        return {
            offensiveGroups: groupByPosition(sortStats(offenseMap), OFFENSE_CATS),
            defensiveGroups: groupByPosition(sortStats(defenseMap, true), DEFENSE_CATS)
        };
    }, [videos, players]);

    if (Object.keys(offensiveGroups).length === 0 && Object.keys(defensiveGroups).length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-[#1a1a1b] rounded-lg border border-gray-800">
                <BarChart3 className="w-12 h-12 text-gray-600 mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">No Stats Available</h3>
                <p className="text-gray-400">Add player stats to individual clips to see them aggregated here.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* OFFENSE TABLES */}
            {offensiveGroups.length > 0 && (
                <Card className="bg-[#1a1a1b] border-gray-800">
                    <CardHeader className="py-4">
                        <CardTitle className="text-sm font-bold text-blue-400 flex items-center gap-2 uppercase tracking-wider">
                            <BarChart3 className="w-4 h-4" />
                            Offensive Leaderboard
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pb-4 space-y-6">
                        {offensiveGroups.map(([groupName, players]) => (
                            <div key={groupName} className="space-y-2">
                                <div className="flex items-center gap-2 px-1 opacity-60">
                                    <Users className="w-2.5 h-2.5 text-gray-500" />
                                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{groupName}</span>
                                </div>
                                <div className="rounded-md border border-gray-800 overflow-hidden">
                                    <Table>
                                        <TableHeader className="bg-[#0a0a0b]">
                                            <TableRow className="border-gray-800 hover:bg-transparent">
                                                <TableHead className="text-gray-400 h-9 text-xs">Player</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">Pass Yds</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">Rush Yds</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">Rec Yds</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">TDs</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {players.map((player) => (
                                                <TableRow key={player.name} className="border-gray-800 hover:bg-gray-800/50">
                                                    <TableCell className="font-medium text-white py-2 text-xs">{player.name}</TableCell>
                                                    <TableCell className="text-right text-gray-300 py-2 text-xs">{player.passing > 0 ? player.passing : '-'}</TableCell>
                                                    <TableCell className="text-right text-gray-300 py-2 text-xs">{player.rushing > 0 ? player.rushing : '-'}</TableCell>
                                                    <TableCell className="text-right text-gray-300 py-2 text-xs">{player.receiving > 0 ? player.receiving : '-'}</TableCell>
                                                    <TableCell className="text-right text-green-400 font-bold py-2 text-xs">{player.touchdowns > 0 ? player.touchdowns : '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* DEFENSE TABLES */}
            {defensiveGroups.length > 0 && (
                <Card className="bg-[#1a1a1b] border-gray-800">
                    <CardHeader className="py-4">
                        <CardTitle className="text-sm font-bold text-red-400 flex items-center gap-2 uppercase tracking-wider">
                            <BarChart3 className="w-4 h-4" />
                            Defensive Leaderboard
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pb-4 space-y-6">
                        {defensiveGroups.map(([groupName, players]) => (
                            <div key={groupName} className="space-y-2">
                                <div className="flex items-center gap-2 px-1 opacity-60">
                                    <Shield className="w-2.5 h-2.5 text-gray-500" />
                                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{groupName}</span>
                                </div>
                                <div className="rounded-md border border-gray-800 overflow-hidden">
                                    <Table>
                                        <TableHeader className="bg-[#0a0a0b]">
                                            <TableRow className="border-gray-800 hover:bg-transparent">
                                                <TableHead className="text-gray-400 h-9 text-xs">Player</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">Tackles</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">Sacks</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">INTs</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">DEFs</TableHead>
                                                <TableHead className="text-gray-400 h-9 text-xs text-right">FFs</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {players.map((player) => (
                                                <TableRow key={player.name} className="border-gray-800 hover:bg-gray-800/50">
                                                    <TableCell className="font-medium text-white py-2 text-xs">{player.name}</TableCell>
                                                    <TableCell className="text-right text-gray-300 py-2 text-xs">{player.tackles > 0 ? player.tackles : '-'}</TableCell>
                                                    <TableCell className="text-right text-gray-300 py-2 text-xs">{player.sacks > 0 ? player.sacks : '-'}</TableCell>
                                                    <TableCell className="text-right text-gray-300 py-2 text-xs">{player.interceptions > 0 ? player.interceptions : '-'}</TableCell>
                                                    <TableCell className="text-right text-gray-300 py-2 text-xs">{player.pass_deflections > 0 ? player.pass_deflections : '-'}</TableCell>
                                                    <TableCell className="text-right text-gray-300 py-2 text-xs">{player.forced_fumbles > 0 ? player.forced_fumbles : '-'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default PlayerStatsTab;
